import { useTranslation } from 'react-i18next'

import { FundingPlan } from '../../../../utils/booking/funding'
import { formatMinutes, formatMoney } from '../../../../utils/booking/booking'

type FundingOptionsProps = {
  plan: Pick<FundingPlan, 'prepaidMinutes' | 'missingMinutes' | 'topUpOptions'>
  topUpPassProductId?: number
  onTopUpChange: (passProductId?: number) => void
  disabled?: boolean
}

/**
 * How the selection is funded: eligible prepaid minutes are always used first (FEFO across passes), and a pass can be
 * bought in the same checkout to cover minutes they are missing.
 */
const FundingOptions = ({
  plan,
  topUpPassProductId,
  onTopUpChange,
  disabled = false,
}: FundingOptionsProps) => {
  const { t } = useTranslation('bookings')
  const { prepaidMinutes, missingMinutes, topUpOptions } = plan
  const showTopUp = (missingMinutes > 0 || topUpPassProductId !== undefined) && topUpOptions.length > 0

  if (prepaidMinutes === 0 && !showTopUp) {
    return null
  }

  return (
    <div className="funding-options mt-16">
      {prepaidMinutes > 0 && (
        <div className="muted mono tiny">
          {t('booking.funding.prepaid-covered', { minutes: formatMinutes(prepaidMinutes) })}
        </div>
      )}

      {showTopUp && (
        <div className="funding-top-up mt-8" role="radiogroup" aria-label={t('booking.funding.top-up-title')}>
          <div className="eyebrow">
            {t('booking.funding.top-up-title', { minutes: formatMinutes(missingMinutes) })}
          </div>
          <label className="funding-option">
            <input
              type="radio"
              name="top-up"
              checked={topUpPassProductId === undefined}
              disabled={disabled}
              onChange={() => onTopUpChange(undefined)}
            />
            <span>{t('booking.funding.pay-per-session')}</span>
          </label>
          {topUpOptions.map(option => (
            <label className="funding-option" key={option.id}>
              <input
                type="radio"
                name="top-up"
                checked={topUpPassProductId === option.id}
                disabled={disabled}
                onChange={() => onTopUpChange(option.id)}
              />
              <span>
                {option.name}
                <span className="muted mono tiny d-block">
                  {t('booking.funding.top-up-option', {
                    minutes: formatMinutes(option.includedMinutes),
                    price: formatMoney(option.price, option.currency),
                    days: option.validityDays,
                  })}
                </span>
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  )
}

export default FundingOptions
